import { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { formatCurrency, formatFileSize, formatCompactNumber } from '@/utils/formatters';
import type { UserType } from '@/types';
import type { UsageSummary } from '@/types/subscription';
import { DEFAULT_IMAGERY_SOURCE } from '@/utils/constants';
import { api } from '@/services/api';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface BackendPricing {
  package: any | null;
  subscriptionId: string | null;
  tokensAvailable: number;
  tokensFromQuota: number;
  tokensOverage: number;
  baseFee: number;
  tilesPrice: number;
  total: number;
  isAuthenticated: boolean;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (jobId: string) => void;
  userType: UserType;
  tileCount: number;
  fileSize: number;
  estimatedTime: number;
  minZoom: number;
  maxZoom: number;
  aoiGeometry: any;
  includeNauticalOverlay: boolean;
  imagerySource?: 'sentinel2' | 'google';
  isAuthenticated?: boolean;
  usage?: UsageSummary;
  backendPricing?: BackendPricing;
  onOrderComplete?: () => void;
}

function StripePaymentStep({
  clientSecret,
  orderId,
  amount,
  onSuccess,
  onError,
}: {
  clientSecret: string;
  orderId: string;
  amount: number;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            borderRadius: '8px',
          },
        },
      }}
    >
      <PaymentForm
        orderId={orderId}
        amount={amount}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
}

function PaymentForm({
  orderId,
  amount,
  onSuccess,
  onError,
}: {
  orderId: string;
  amount: number;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isConfirming, setIsConfirming] = useState(false);

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsConfirming(true);

    const { error: submitError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard/orders?payment=success`,
      },
      redirect: 'if_required',
    });

    if (submitError) {
      onError(submitError.message ?? 'Payment failed. Please try again.');
      setIsConfirming(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        await api.confirmOrder(orderId, paymentIntent.id);
        onSuccess();
      } catch {
        onError('Payment succeeded but order confirmation failed. Please contact support.');
      }
    } else {
      onError(`Unexpected payment status: ${paymentIntent?.status}. Please try again.`);
      setIsConfirming(false);
    }
  };

  return (
    <form onSubmit={handlePayment} className="space-y-4">
      <div className="rounded-lg border border-gray-200 p-4 bg-white">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      <div className="flex items-center space-x-2 text-xs text-gray-500 justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
          <path fillRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clipRule="evenodd" />
        </svg>
        <span>Payments are secure and encrypted via Stripe</span>
      </div>

      <Button
        type="submit"
        variant="accent"
        fullWidth
        loading={isConfirming}
        disabled={!stripe || !elements || isConfirming}
      >
        {isConfirming ? 'Processing...' : `Pay ${formatCurrency(amount / 100)}`}
      </Button>
    </form>
  );
}

export function PaymentModal({
  isOpen,
  onClose,
  onSuccess,
  userType: _userType,
  tileCount,
  fileSize,
  estimatedTime,
  minZoom,
  maxZoom,
  aoiGeometry,
  includeNauticalOverlay,
  imagerySource = DEFAULT_IMAGERY_SOURCE as 'sentinel2' | 'google',
  isAuthenticated = false,
  usage,
  backendPricing,
  onOrderComplete,
}: PaymentModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const [paymentStep, setPaymentStep] = useState<{
    clientSecret: string;
    orderId: string;
  } | null>(null);

  if (!isOpen) return null;

  const effectiveTileCount = includeNauticalOverlay ? tileCount * 2 : tileCount;

  const displayPricing = backendPricing || {
    baseFee: 5000,
    tilesPrice: Math.ceil(effectiveTileCount / 1000) * 40,
    total: 5000 + Math.ceil(effectiveTileCount / 1000) * 40,
    tokensFromQuota: 0,
    tokensOverage: effectiveTileCount,
  };

  const isFreeWithSubscription = isAuthenticated && displayPricing.total === 0 && displayPricing.tokensFromQuota > 0;
  const requiresPayment = displayPricing.total > 0;

  const isValidEmail = (emailVal: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailVal);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError('');
  };

  const handleConfirmEmailChange = (value: string) => {
    setConfirmEmail(value);
    if (value && email && value !== email) {
      setEmailError('Email addresses do not match');
    } else {
      setEmailError('');
    }
  };

  const getAOIBounds = (geometry: any): number[] => {
    if (!geometry || !geometry.rings || !geometry.rings[0]) {
      return [0, 0, 0, 0];
    }

    const coords = geometry.rings[0];
    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    for (const [lng, lat] of coords) {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }

    return [minLng, minLat, maxLng, maxLat];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isAuthenticated) {
      if (!email || !confirmEmail) {
        setEmailError('Email address is required');
        return;
      }

      if (!isValidEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }

      if (email !== confirmEmail) {
        setEmailError('Email addresses do not match');
        return;
      }

      const confirmed = window.confirm(
        `We'll send your download link to:\n\n${email}\n\nPlease verify this is correct before continuing.`
      );

      if (!confirmed) {
        return;
      }
    }

    setIsProcessing(true);

    try {
      if (isAuthenticated) {
        const aoiBounds = getAOIBounds(aoiGeometry);

        const orderResult = await api.createOrder({
          aoiGeometry,
          aoiBounds,
          minZoom,
          maxZoom,
          tileCount: effectiveTileCount,
          includeNauticalOverlay,
          imagerySource,
          notificationEmail: email || undefined,
        });

        if (!orderResult.requiresPayment) {
          onOrderComplete?.();
          onSuccess(orderResult.order.id);
          return;
        }

        if (orderResult.clientSecret) {
          setPaymentStep({
            clientSecret: orderResult.clientSecret,
            orderId: orderResult.order.id,
          });
          setIsProcessing(false);
          return;
        }

        onOrderComplete?.();
        onSuccess(orderResult.order.id);
      } else {
        // Non-authenticated legacy flow — redirect to Stripe Checkout would go here
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const mockJobId = `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        onSuccess(mockJobId);
      }
    } catch (err: any) {
      console.error('[PaymentModal] Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create order. Please try again.');
      setIsProcessing(false);
    }
  };

  const handlePaymentSuccess = useCallback(() => {
    onOrderComplete?.();
    onSuccess(paymentStep!.orderId);
  }, [paymentStep, onOrderComplete, onSuccess]);

  const handlePaymentError = useCallback((msg: string) => {
    setError(msg);
  }, []);

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {paymentStep
                ? 'Complete Payment'
                : isFreeWithSubscription
                ? 'Confirm Order'
                : 'Complete Your Purchase'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isProcessing}
            >
              &times;
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Subscription Benefit Banner */}
          {isFreeWithSubscription && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">&#127881;</span>
                <div>
                  <h4 className="font-semibold text-green-800">Covered by Your Subscription!</h4>
                  <p className="text-sm text-green-700">
                    This order uses {formatCompactNumber(displayPricing.tokensFromQuota)} tokens from your {usage?.package?.name} plan.
                    No payment required.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <Card variant="bordered" padding="lg" className="bg-gray-50">
            <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Tile Count</span>
                <span className="font-semibold text-gray-900">{formatCompactNumber(tileCount)}</span>
              </div>
              {includeNauticalOverlay && (
                <>
                  <div className="flex justify-between text-xs">
                    <span className="text-blue-600">+ Nautical tiles</span>
                    <span className="text-blue-600 font-semibold">{formatCompactNumber(tileCount)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="text-gray-700 font-semibold">Total Tokens</span>
                    <span className="font-bold text-gray-900">{formatCompactNumber(effectiveTileCount)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Zoom Levels</span>
                <span className="font-semibold text-gray-900">{minZoom} - {maxZoom}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Estimated File Size</span>
                <span className="font-semibold text-gray-900">{formatFileSize(fileSize)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Generation Time</span>
                <span className="font-semibold text-gray-900">~{formatTime(estimatedTime)}</span>
              </div>

              {isAuthenticated && displayPricing.tokensFromQuota > 0 && (
                <div className="pt-3 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-green-600">From Subscription</span>
                    <span className="font-semibold text-green-600">
                      -{formatCompactNumber(displayPricing.tokensFromQuota)} tokens
                    </span>
                  </div>
                  {displayPricing.tokensOverage > 0 && (
                    <div className="flex justify-between">
                      <span className="text-amber-600">Overage Tiles</span>
                      <span className="font-semibold text-amber-600">
                        {formatCompactNumber(displayPricing.tokensOverage)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-3 border-t border-gray-300 space-y-2">
                {!isFreeWithSubscription && (
                  <>
                    {displayPricing.baseFee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Fee</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(displayPricing.baseFee / 100)}
                        </span>
                      </div>
                    )}
                    {displayPricing.tilesPrice > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {displayPricing.tokensFromQuota > 0 ? 'Overage Cost' : 'Tiles Cost'}
                        </span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(displayPricing.tilesPrice / 100)}
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between text-lg pt-2 border-t border-gray-300">
                  <span className="font-bold text-gray-900">Total</span>
                  {isFreeWithSubscription ? (
                    <span className="font-bold text-green-600">FREE</span>
                  ) : (
                    <span className="font-bold text-primary-600">
                      {formatCurrency(displayPricing.total / 100)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Delivery Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Delivery Information</h4>
            <p className="text-sm text-blue-800">
              {isAuthenticated
                ? estimatedTime < 60
                  ? "Your MBTiles file will be ready for immediate download after confirmation."
                  : "We'll email you a download link when your MBTiles file is ready. You can also track progress in your dashboard."
                : "We'll email you a download link when your chart is ready. Please enter your email address carefully - this is the only way we can send you the download link."
              }
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Stripe PaymentElement step — shown after order is created */}
          {paymentStep && (
            <StripePaymentStep
              clientSecret={paymentStep.clientSecret}
              orderId={paymentStep.orderId}
              amount={displayPricing.total}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          )}

          {/* Order form — hidden once we're in the payment step */}
          {!paymentStep && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Section for Non-authenticated Users */}
              {!isAuthenticated && (
                <Card variant="bordered" padding="lg" className="bg-gradient-to-br from-blue-50 to-indigo-50">
                  <h3 className="font-semibold text-gray-900 mb-4">Email Address (Required)</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    We'll send your download link to this email. <strong>Please double-check for accuracy!</strong>
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        placeholder="your.email@example.com"
                        value={email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          emailError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                        disabled={isProcessing}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Email Address *
                      </label>
                      <input
                        type="email"
                        placeholder="your.email@example.com"
                        value={confirmEmail}
                        onChange={(e) => handleConfirmEmailChange(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          emailError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                        disabled={isProcessing}
                      />
                    </div>

                    {emailError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-800">{emailError}</p>
                      </div>
                    )}

                    {email && confirmEmail && !emailError && email === confirmEmail && isValidEmail(email) && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-800">
                          Download link will be sent to: <strong>{email}</strong>
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={onClose}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="accent"
                  fullWidth
                  loading={isProcessing}
                >
                  {isProcessing
                    ? 'Creating Order...'
                    : isFreeWithSubscription
                    ? 'Confirm Order'
                    : requiresPayment
                    ? 'Continue to Payment'
                    : `Pay ${formatCurrency(displayPricing.total / 100)}`
                  }
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
